import React, { useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ArrowDropDown as ArrowDropDownIcon,
  CheckCircle as CheckCircleIcon,
  Description as CsvIcon,
  Download as DownloadIcon,
  PictureAsPdf as PdfIcon,
  Print as PrintIcon,
  Refresh as RefreshIcon,
  TableChart as ExcelIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import moment from 'moment-timezone';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { CampaignFilterService } from '../services/campaignFilterService';
import { Campaign, User } from '../types';
import { ATTENDANCE_CHIP_COLORS, OPS_COLORS } from '../constants/operationsDashboardColors';
import {
  exportWeeklyStaffingCoverageToCSV,
  exportWeeklyStaffingCoverageToExcel,
  exportWeeklyStaffingCoverageToPDF,
  printWeeklyStaffingCoverageReport,
} from '../utils/weeklyStaffingCoverageExport';

const ATTENDANCE_FILTER_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'present', label: 'Present' },
  { value: 'late', label: 'Late' },
  { value: 'absent', label: 'Absent' },
  { value: 'no_show', label: 'No Show' },
  { value: 'scheduled_off', label: 'Scheduled Off' },
];

const COVERAGE_FILTER_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'covered', label: 'Covered' },
  { value: 'not_covered', label: 'Not Covered' },
  { value: 'partially_covered', label: 'Partially Covered' },
  { value: 'not_required', label: 'Not Required' },
];

const COVERAGE_MODE_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'full_transfer', label: 'Full Transfer' },
  { value: 'shared_coverage', label: 'Shared Coverage' },
];

const NOTIFICATION_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'notified', label: 'Customer Notified' },
  { value: 'not_notified', label: 'Not Notified' },
];

const INVOICE_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'ready', label: 'Ready for Billing' },
  { value: 'excluded', label: 'Excluded' },
  { value: 'invoiced', label: 'Already Invoiced' },
];

function getWeekDates(weekStart: string): string[] {
  return Array.from({ length: 7 }, (_, i) => moment(weekStart).add(i, 'days').format('YYYY-MM-DD'));
}

type SortDirection = 'asc' | 'desc';

const round2 = (value: number) => Math.round(value * 100) / 100;

function sortRows<T>(
  rows: T[],
  field: string,
  direction: SortDirection,
  getValue: (row: T, field: string) => string | number,
): T[] {
  const multiplier = direction === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const aValue = getValue(a, field);
    const bValue = getValue(b, field);
    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return (aValue - bValue) * multiplier;
    }
    return String(aValue).localeCompare(String(bValue), undefined, { numeric: true }) * multiplier;
  });
}

function getCampaignSortValue(row: any, field: string): string | number {
  switch (field) {
    case 'campaignName': return row.campaignName || '';
    case 'scheduledHours': return row.scheduledHours || 0;
    case 'workedHours': return row.workedHours || 0;
    case 'coverageHours': return row.coverageHours || 0;
    case 'coverageReceived': return row.coverageReceived || 0;
    case 'coverageGiven': return row.coverageGiven || 0;
    case 'coveragePercent': return row.coveragePercent || 0;
    case 'billableHours': return row.billableHours || 0;
    default: return '';
  }
}

function getSupervisorSortValue(row: any, field: string): string | number {
  switch (field) {
    case 'supervisorName': return row.supervisorName || '';
    case 'employeesManaged': return row.employeesManaged || 0;
    case 'coverageAssignments': return row.coverageAssignments || 0;
    case 'transfers': return row.transfers || 0;
    case 'lateEmployees': return row.lateEmployees || 0;
    case 'noShows': return row.noShows || 0;
    case 'notificationsSent': return row.notificationsSent || 0;
    default: return '';
  }
}

function getWeekRowSortValue(row: any, field: string): string | number {
  if (field.startsWith('day:')) {
    const date = field.slice(4);
    const cell = row.days?.[date];
    return cell?.billingHours || cell?.hoursWorked || 0;
  }
  switch (field) {
    case 'employeeName': return row.employeeName || '';
    case 'campaignName': return row.campaignName || '';
    case 'scheduledHours': return row.weeklyTotals?.scheduledHours || 0;
    case 'workedHours': return row.weeklyTotals?.workedHours || 0;
    case 'billableHours': return row.weeklyTotals?.billableHours || 0;
    case 'coverageHours': return row.weeklyTotals?.coverageHours || 0;
    case 'overtimeHours': return row.weeklyTotals?.overtimeHours || 0;
    default: return '';
  }
}

function buildCampaignTotals(rows: any[]) {
  const totals = rows.reduce((acc, row) => ({
    scheduledHours: acc.scheduledHours + (row.scheduledHours || 0),
    workedHours: acc.workedHours + (row.workedHours || 0),
    coverageHours: acc.coverageHours + (row.coverageHours || 0),
    coverageReceived: acc.coverageReceived + (row.coverageReceived || 0),
    coverageGiven: acc.coverageGiven + (row.coverageGiven || 0),
    billableHours: acc.billableHours + (row.billableHours || 0),
  }), {
    scheduledHours: 0,
    workedHours: 0,
    coverageHours: 0,
    coverageReceived: 0,
    coverageGiven: 0,
    billableHours: 0,
  });

  return {
    scheduledHours: round2(totals.scheduledHours),
    workedHours: round2(totals.workedHours),
    coverageHours: round2(totals.coverageHours),
    coverageReceived: round2(totals.coverageReceived),
    coverageGiven: round2(totals.coverageGiven),
    billableHours: round2(totals.billableHours),
    coveragePercent: totals.scheduledHours > 0
      ? Math.round((totals.coverageHours / totals.scheduledHours) * 100)
      : 0,
  };
}

function buildSupervisorTotals(rows: any[]) {
  return rows.reduce((acc, row) => ({
    employeesManaged: acc.employeesManaged + (row.employeesManaged || 0),
    coverageAssignments: acc.coverageAssignments + (row.coverageAssignments || 0),
    transfers: acc.transfers + (row.transfers || 0),
    lateEmployees: acc.lateEmployees + (row.lateEmployees || 0),
    noShows: acc.noShows + (row.noShows || 0),
    notificationsSent: acc.notificationsSent + (row.notificationsSent || 0),
  }), {
    employeesManaged: 0,
    coverageAssignments: 0,
    transfers: 0,
    lateEmployees: 0,
    noShows: 0,
    notificationsSent: 0,
  });
}

function buildWeekTotals(rows: any[], weekDates: string[]) {
  const dayTotals = Object.fromEntries(weekDates.map((date) => [date, { billingHours: 0, hoursWorked: 0 }]));

  const totals = rows.reduce((acc, row) => {
    acc.scheduledHours += row.weeklyTotals?.scheduledHours || 0;
    acc.workedHours += row.weeklyTotals?.workedHours || 0;
    acc.billableHours += row.weeklyTotals?.billableHours || 0;
    acc.coverageHours += row.weeklyTotals?.coverageHours || 0;
    acc.overtimeHours += row.weeklyTotals?.overtimeHours || 0;

    weekDates.forEach((date) => {
      const cell = row.days?.[date];
      if (!cell) return;
      dayTotals[date].billingHours += cell.billingHours || 0;
      dayTotals[date].hoursWorked += cell.hoursWorked || 0;
    });

    return acc;
  }, {
    scheduledHours: 0,
    workedHours: 0,
    billableHours: 0,
    coverageHours: 0,
    overtimeHours: 0,
  });

  return {
    scheduledHours: round2(totals.scheduledHours),
    workedHours: round2(totals.workedHours),
    billableHours: round2(totals.billableHours),
    coverageHours: round2(totals.coverageHours),
    overtimeHours: round2(totals.overtimeHours),
    dayTotals: Object.fromEntries(
      weekDates.map((date) => [
        date,
        {
          billingHours: round2(dayTotals[date].billingHours),
          hoursWorked: round2(dayTotals[date].hoursWorked),
        },
      ]),
    ),
  };
}

interface SortableHeaderCellProps {
  label: string;
  field: string;
  activeField: string;
  direction: SortDirection;
  onSort: (field: string) => void;
  align?: 'left' | 'right' | 'center';
  sx?: object;
}

const SortableHeaderCell: React.FC<SortableHeaderCellProps> = ({
  label,
  field,
  activeField,
  direction,
  onSort,
  align = 'left',
  sx,
}) => (
  <TableCell align={align} sortDirection={activeField === field ? direction : false} sx={sx}>
    <TableSortLabel
      active={activeField === field}
      direction={activeField === field ? direction : 'asc'}
      onClick={() => onSort(field)}
    >
      {label}
    </TableSortLabel>
  </TableCell>
);

const WeeklyStaffingCoverageReportPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [startDate, setStartDate] = useState(moment().startOf('isoWeek').format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState(moment().endOf('isoWeek').format('YYYY-MM-DD'));
  const [selectedCampaigns, setSelectedCampaigns] = useState<Campaign[]>([]);
  const [selectedEmployees, setSelectedEmployees] = useState<User[]>([]);
  const [supervisorId, setSupervisorId] = useState('');
  const [attendanceFilter, setAttendanceFilter] = useState('all');
  const [coverageFilter, setCoverageFilter] = useState('all');
  const [coverageModeFilter, setCoverageModeFilter] = useState('all');
  const [notificationFilter, setNotificationFilter] = useState('all');
  const [invoiceFilter, setInvoiceFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [expandedCell, setExpandedCell] = useState<any | null>(null);
  const [exportMenuAnchor, setExportMenuAnchor] = useState<null | HTMLElement>(null);
  const [showCampaignSummary, setShowCampaignSummary] = useState(true);
  const [showSupervisorSummary, setShowSupervisorSummary] = useState(true);
  const [campaignSortField, setCampaignSortField] = useState('campaignName');
  const [campaignSortDirection, setCampaignSortDirection] = useState<SortDirection>('asc');
  const [supervisorSortField, setSupervisorSortField] = useState('supervisorName');
  const [supervisorSortDirection, setSupervisorSortDirection] = useState<SortDirection>('asc');
  const [weekSortField, setWeekSortField] = useState('employeeName');
  const [weekSortDirection, setWeekSortDirection] = useState<SortDirection>('asc');

  const { data: employeesData } = useQuery({
    queryKey: ['employees'],
    queryFn: () => apiClient.users.getAll(),
    enabled: !!user,
  });

  const { data: campaignsData } = useQuery({
    queryKey: ['campaigns', startDate, endDate],
    queryFn: () => apiClient.campaigns.getAll({
      includeWithActivityFrom: startDate,
      includeWithActivityTo: endDate,
    }),
    enabled: !!user,
  });

  const availableCampaigns = campaignsData?.data?.data
    ? CampaignFilterService.getUserAccessibleCampaigns(user!, campaignsData.data.data)
    : [];

  const employees: User[] = employeesData?.data?.data || [];
  const supervisors: User[] = employees.filter(
    (u) => ['supervisor', 'team_lead', 'manager'].includes(u.role),
  );

  const queryParams = useMemo(() => ({
    startDate,
    endDate,
    campaignIds: selectedCampaigns.map((c) => c.id).join(',') || undefined,
    employeeIds: selectedEmployees.map((e) => e.id).join(',') || undefined,
    supervisorId: supervisorId || undefined,
    attendanceStatus: attendanceFilter !== 'all' ? attendanceFilter : undefined,
    coverageStatus: coverageFilter !== 'all' ? coverageFilter : undefined,
    coverageMode: coverageModeFilter !== 'all' ? coverageModeFilter : undefined,
    notificationStatus: notificationFilter !== 'all' ? notificationFilter : undefined,
    invoiceStatus: invoiceFilter !== 'all' ? invoiceFilter : undefined,
    page: page + 1,
    limit: rowsPerPage,
  }), [
    startDate, endDate, selectedCampaigns, selectedEmployees, supervisorId,
    attendanceFilter, coverageFilter, coverageModeFilter, notificationFilter,
    invoiceFilter, page, rowsPerPage,
  ]);

  const { data: reportResponse, isLoading, error, refetch } = useQuery({
    queryKey: ['weekly-staffing-coverage', queryParams],
    queryFn: () => apiClient.reports.getWeeklyStaffingCoverage(queryParams).then((res) => res.data),
    enabled: !!user && !!startDate && !!endDate,
  });

  const report = reportResponse?.data;

  const handleCampaignSort = (field: string) => {
    if (campaignSortField === field) {
      setCampaignSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setCampaignSortField(field);
    setCampaignSortDirection('asc');
  };

  const handleSupervisorSort = (field: string) => {
    if (supervisorSortField === field) {
      setSupervisorSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSupervisorSortField(field);
    setSupervisorSortDirection('asc');
  };

  const handleWeekSort = (field: string) => {
    if (weekSortField === field) {
      setWeekSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setWeekSortField(field);
    setWeekSortDirection('asc');
  };

  const sortedCampaignSummary = useMemo(() => {
    if (!report?.campaignSummary) return [];
    return sortRows(report.campaignSummary, campaignSortField, campaignSortDirection, getCampaignSortValue);
  }, [report?.campaignSummary, campaignSortField, campaignSortDirection]);

  const campaignTotals = useMemo(
    () => buildCampaignTotals(report?.campaignSummary || []),
    [report?.campaignSummary],
  );

  const sortedSupervisorSummary = useMemo(() => {
    if (!report?.supervisorSummary) return [];
    return sortRows(report.supervisorSummary, supervisorSortField, supervisorSortDirection, getSupervisorSortValue);
  }, [report?.supervisorSummary, supervisorSortField, supervisorSortDirection]);

  const supervisorTotals = useMemo(
    () => buildSupervisorTotals(report?.supervisorSummary || []),
    [report?.supervisorSummary],
  );

  const handleExport = (format: 'csv' | 'excel' | 'pdf' | 'print') => {
    if (!report) return;
    if (format === 'csv') exportWeeklyStaffingCoverageToCSV(report);
    else if (format === 'excel') exportWeeklyStaffingCoverageToExcel(report);
    else if (format === 'pdf') exportWeeklyStaffingCoverageToPDF(report);
    else printWeeklyStaffingCoverageReport(report);
    setExportMenuAnchor(null);
  };

  const drillEmployee = (employeeId: string, date: string) => {
    navigate(`/operations/attendance-coverage?date=${date}&employeeId=${employeeId}`);
  };

  const drillCampaign = (campaignId: string, date: string) => {
    navigate(`/operations/attendance-coverage?date=${date}&campaignId=${campaignId}`);
  };

  const renderDayCell = (cell: any | null, row: any, date: string) => {
    if (!cell) {
      return <TableCell key={date} sx={{ bgcolor: '#fafafa', minWidth: 110 }} />;
    }

    const attendanceColor = ATTENDANCE_CHIP_COLORS[cell.attendanceStatus] || OPS_COLORS.expected;
    const hasCoverage = cell.coverageStatus === 'COVERED' || cell.coverageStatus === 'PARTIALLY_COVERED';

    return (
      <TableCell
        key={date}
        onClick={() => setExpandedCell({ ...cell, employeeName: row.employeeName, campaignName: row.campaignName, employeeId: row.employeeId, campaignId: row.campaignId })}
        sx={{
          cursor: 'pointer',
          minWidth: 110,
          verticalAlign: 'top',
          borderLeft: '1px solid #eee',
          '&:hover': { bgcolor: '#f5f5f5' },
        }}
      >
        <Stack spacing={0.3}>
          <Typography variant="caption" color="text.secondary" display="block">
            {cell.scheduleStart && cell.scheduleEnd ? `${cell.scheduleStart}–${cell.scheduleEnd}` : '—'}
          </Typography>
          <Typography variant="caption" display="block">
            {cell.clockIn || '—'} / {cell.clockOut || '—'}
          </Typography>
          <Chip
            label={cell.attendanceLabel}
            size="small"
            sx={{ height: 18, fontSize: '0.65rem', bgcolor: attendanceColor, color: '#fff' }}
          />
          {hasCoverage && (
            <Chip
              label={cell.coverageModeLabel || 'Coverage'}
              size="small"
              sx={{
                height: 18,
                fontSize: '0.65rem',
                bgcolor: cell.coverageMode === 'FULL_TRANSFER' ? '#7b1fa2' : '#00838f',
                color: '#fff',
              }}
            />
          )}
          {cell.coverageMode === 'SHARED_COVERAGE' && !hasCoverage && (
            <Chip
              label="Shared Coverage"
              size="small"
              sx={{
                height: 18,
                fontSize: '0.65rem',
                bgcolor: '#00838f',
                color: '#fff',
              }}
            />
          )}
          {cell.hoursWorked > 0 && (
            <Typography variant="caption" display="block">
              Worked: {cell.hoursWorked}h
            </Typography>
          )}
          <Typography
            variant="caption"
            display="block"
            color={cell.billable ? 'success.main' : 'text.secondary'}
            fontWeight={cell.billable ? 600 : 400}
          >
            Billable: {cell.billingHours}h
          </Typography>
          {cell.customerNotified ? (
            <CheckCircleIcon sx={{ fontSize: 14, color: OPS_COLORS.present }} />
          ) : hasCoverage ? (
            <WarningIcon sx={{ fontSize: 14, color: OPS_COLORS.critical }} />
          ) : null}
        </Stack>
      </TableCell>
    );
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 3, mb: 4 }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h4" gutterBottom>
          Weekly Staffing & Coverage Report
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Weekly Workforce Activity Report (Customer Billing)
        </Typography>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                label="Start Date"
                type="date"
                fullWidth
                size="small"
                value={startDate}
                onChange={(e) => { setStartDate(e.target.value); setPage(0); }}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                label="End Date"
                type="date"
                fullWidth
                size="small"
                value={endDate}
                onChange={(e) => { setEndDate(e.target.value); setPage(0); }}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Autocomplete
                multiple
                size="small"
                options={availableCampaigns}
                getOptionLabel={(o) => o.name}
                value={selectedCampaigns}
                onChange={(_, v) => { setSelectedCampaigns(v); setPage(0); }}
                renderInput={(params) => <TextField {...params} label="Campaign" />}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Autocomplete
                multiple
                size="small"
                options={employees}
                getOptionLabel={(o) => `${o.firstName} ${o.lastName}`}
                value={selectedEmployees}
                onChange={(_, v) => { setSelectedEmployees(v); setPage(0); }}
                renderInput={(params) => <TextField {...params} label="Employee" />}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                select
                fullWidth
                size="small"
                label="Supervisor"
                value={supervisorId}
                onChange={(e) => { setSupervisorId(e.target.value); setPage(0); }}
              >
                <MenuItem value="">All</MenuItem>
                {supervisors.map((s) => (
                  <MenuItem key={s.id} value={s.id}>{s.firstName} {s.lastName}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <TextField select fullWidth size="small" label="Attendance" value={attendanceFilter}
                onChange={(e) => { setAttendanceFilter(e.target.value); setPage(0); }}>
                {ATTENDANCE_FILTER_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <TextField select fullWidth size="small" label="Coverage" value={coverageFilter}
                onChange={(e) => { setCoverageFilter(e.target.value); setPage(0); }}>
                {COVERAGE_FILTER_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <TextField select fullWidth size="small" label="Coverage Mode" value={coverageModeFilter}
                onChange={(e) => { setCoverageModeFilter(e.target.value); setPage(0); }}>
                {COVERAGE_MODE_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <TextField select fullWidth size="small" label="Notification" value={notificationFilter}
                onChange={(e) => { setNotificationFilter(e.target.value); setPage(0); }}>
                {NOTIFICATION_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <TextField select fullWidth size="small" label="Invoice Status" value={invoiceFilter}
                onChange={(e) => { setInvoiceFilter(e.target.value); setPage(0); }}>
                {INVOICE_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <Stack direction="row" spacing={1}>
                <Button variant="contained" startIcon={<RefreshIcon />} onClick={() => refetch()}>
                  Run Report
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  endIcon={<ArrowDropDownIcon />}
                  onClick={(e) => setExportMenuAnchor(e.currentTarget)}
                  disabled={!report}
                >
                  Export
                </Button>
                <Menu anchorEl={exportMenuAnchor} open={Boolean(exportMenuAnchor)} onClose={() => setExportMenuAnchor(null)}>
                  <MenuItem onClick={() => handleExport('pdf')}><ListItemIcon><PdfIcon /></ListItemIcon><ListItemText>PDF</ListItemText></MenuItem>
                  <MenuItem onClick={() => handleExport('excel')}><ListItemIcon><ExcelIcon /></ListItemIcon><ListItemText>Excel</ListItemText></MenuItem>
                  <MenuItem onClick={() => handleExport('csv')}><ListItemIcon><CsvIcon /></ListItemIcon><ListItemText>CSV</ListItemText></MenuItem>
                  <MenuItem onClick={() => handleExport('print')}><ListItemIcon><PrintIcon /></ListItemIcon><ListItemText>Print</ListItemText></MenuItem>
                </Menu>
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {error && <Alert severity="error" sx={{ mb: 2 }}>Failed to load report.</Alert>}
      {isLoading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>}

      {report && !isLoading && (
        <>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Summary</Typography>
              <Grid container spacing={2}>
                {[
                  ['Employees', report.summary.employeeCount],
                  ['Scheduled Hours', report.summary.scheduledHours],
                  ['Worked Hours', report.summary.workedHours],
                  ['Coverage Hours', report.summary.coverageHours],
                  ['Coverage Assignments', report.summary.coverageAssignments],
                  ['Transfers', report.summary.coverageTransfers],
                  ['Shared Coverage', report.summary.sharedCoverage],
                  ['Customer Notifications', report.summary.customerNotifications],
                  ['Billable Hours', report.summary.billableHours],
                  ['Operational Readiness', `${report.summary.operationalReadinessAverage}%`],
                ].map(([label, value]) => (
                  <Grid item xs={6} sm={4} md={2} key={String(label)}>
                    <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
                      <Typography variant="caption" color="text.secondary">{label}</Typography>
                      <Typography variant="h6">{value}</Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>

          <Button size="small" onClick={() => setShowCampaignSummary(!showCampaignSummary)} sx={{ mb: 1 }}>
            {showCampaignSummary ? 'Hide' : 'Show'} Campaign Summary
          </Button>
          <Collapse in={showCampaignSummary}>
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <SortableHeaderCell label="Campaign" field="campaignName" activeField={campaignSortField} direction={campaignSortDirection} onSort={handleCampaignSort} />
                      <SortableHeaderCell label="Scheduled Hrs" field="scheduledHours" activeField={campaignSortField} direction={campaignSortDirection} onSort={handleCampaignSort} align="right" />
                      <SortableHeaderCell label="Worked Hrs" field="workedHours" activeField={campaignSortField} direction={campaignSortDirection} onSort={handleCampaignSort} align="right" />
                      <SortableHeaderCell label="Coverage Hrs" field="coverageHours" activeField={campaignSortField} direction={campaignSortDirection} onSort={handleCampaignSort} align="right" />
                      <SortableHeaderCell label="Received Hrs" field="coverageReceived" activeField={campaignSortField} direction={campaignSortDirection} onSort={handleCampaignSort} align="right" />
                      <SortableHeaderCell label="Given Hrs" field="coverageGiven" activeField={campaignSortField} direction={campaignSortDirection} onSort={handleCampaignSort} align="right" />
                      <SortableHeaderCell label="Coverage %" field="coveragePercent" activeField={campaignSortField} direction={campaignSortDirection} onSort={handleCampaignSort} align="right" />
                      <SortableHeaderCell label="Billable Hrs" field="billableHours" activeField={campaignSortField} direction={campaignSortDirection} onSort={handleCampaignSort} align="right" />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedCampaignSummary.map((c: any) => (
                      <TableRow key={c.campaignId} hover sx={{ cursor: 'pointer' }}
                        onClick={() => drillCampaign(c.campaignId, report.dateRange.startDate)}>
                        <TableCell>{c.campaignName}</TableCell>
                        <TableCell align="right">{c.scheduledHours}</TableCell>
                        <TableCell align="right">{c.workedHours}</TableCell>
                        <TableCell align="right">{c.coverageHours}</TableCell>
                        <TableCell align="right">{c.coverageReceived}</TableCell>
                        <TableCell align="right">{c.coverageGiven}</TableCell>
                        <TableCell align="right">{c.coveragePercent}%</TableCell>
                        <TableCell align="right">{c.billableHours}</TableCell>
                      </TableRow>
                    ))}
                    {sortedCampaignSummary.length > 0 && (
                      <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                        <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>{campaignTotals.scheduledHours}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>{campaignTotals.workedHours}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>{campaignTotals.coverageHours}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>{campaignTotals.coverageReceived}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>{campaignTotals.coverageGiven}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>{campaignTotals.coveragePercent}%</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>{campaignTotals.billableHours}</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </Collapse>

          <Button size="small" onClick={() => setShowSupervisorSummary(!showSupervisorSummary)} sx={{ mb: 1 }}>
            {showSupervisorSummary ? 'Hide' : 'Show'} Supervisor Summary
          </Button>
          <Collapse in={showSupervisorSummary}>
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <SortableHeaderCell label="Supervisor" field="supervisorName" activeField={supervisorSortField} direction={supervisorSortDirection} onSort={handleSupervisorSort} />
                      <SortableHeaderCell label="Employees" field="employeesManaged" activeField={supervisorSortField} direction={supervisorSortDirection} onSort={handleSupervisorSort} align="right" />
                      <SortableHeaderCell label="Coverage" field="coverageAssignments" activeField={supervisorSortField} direction={supervisorSortDirection} onSort={handleSupervisorSort} align="right" />
                      <SortableHeaderCell label="Transfers" field="transfers" activeField={supervisorSortField} direction={supervisorSortDirection} onSort={handleSupervisorSort} align="right" />
                      <SortableHeaderCell label="Late" field="lateEmployees" activeField={supervisorSortField} direction={supervisorSortDirection} onSort={handleSupervisorSort} align="right" />
                      <SortableHeaderCell label="No Shows" field="noShows" activeField={supervisorSortField} direction={supervisorSortDirection} onSort={handleSupervisorSort} align="right" />
                      <SortableHeaderCell label="Notifications" field="notificationsSent" activeField={supervisorSortField} direction={supervisorSortDirection} onSort={handleSupervisorSort} align="right" />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedSupervisorSummary.map((s: any) => (
                      <TableRow key={s.supervisorId}>
                        <TableCell>{s.supervisorName}</TableCell>
                        <TableCell align="right">{s.employeesManaged}</TableCell>
                        <TableCell align="right">{s.coverageAssignments}</TableCell>
                        <TableCell align="right">{s.transfers}</TableCell>
                        <TableCell align="right">{s.lateEmployees}</TableCell>
                        <TableCell align="right">{s.noShows}</TableCell>
                        <TableCell align="right">{s.notificationsSent}</TableCell>
                      </TableRow>
                    ))}
                    {sortedSupervisorSummary.length > 0 && (
                      <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                        <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>{supervisorTotals.employeesManaged}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>{supervisorTotals.coverageAssignments}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>{supervisorTotals.transfers}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>{supervisorTotals.lateEmployees}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>{supervisorTotals.noShows}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>{supervisorTotals.notificationsSent}</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </Collapse>

          {report.weeks.map((week: any) => {
            const weekDates = getWeekDates(week.weekStart);
            const sortedWeekRows = sortRows(week.rows, weekSortField, weekSortDirection, getWeekRowSortValue);
            const weekTotals = buildWeekTotals(week.rows, weekDates);
            return (
              <Card key={week.weekStart} sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Week of {moment(week.weekStart).format('MMM D')} – {moment(week.weekEnd).format('MMM D, YYYY')}
                  </Typography>
                  <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <SortableHeaderCell
                            label="Employee"
                            field="employeeName"
                            activeField={weekSortField}
                            direction={weekSortDirection}
                            onSort={handleWeekSort}
                            sx={{ minWidth: 140, position: 'sticky', left: 0, bgcolor: '#fff', zIndex: 3 }}
                          />
                          <SortableHeaderCell
                            label="Campaign"
                            field="campaignName"
                            activeField={weekSortField}
                            direction={weekSortDirection}
                            onSort={handleWeekSort}
                            sx={{ minWidth: 120, position: 'sticky', left: 140, bgcolor: '#fff', zIndex: 3 }}
                          />
                          <SortableHeaderCell
                            label="Scheduled Hrs"
                            field="scheduledHours"
                            activeField={weekSortField}
                            direction={weekSortDirection}
                            onSort={handleWeekSort}
                            align="right"
                            sx={{ minWidth: 100, position: 'sticky', left: 260, bgcolor: '#fff', zIndex: 3 }}
                          />
                          <SortableHeaderCell
                            label="Billable Hrs"
                            field="billableHours"
                            activeField={weekSortField}
                            direction={weekSortDirection}
                            onSort={handleWeekSort}
                            align="right"
                            sx={{ minWidth: 100, position: 'sticky', left: 360, bgcolor: '#fff', zIndex: 3 }}
                          />
                          {weekDates.map((d) => (
                            <SortableHeaderCell
                              key={d}
                              label={moment(d).format('ddd M/D')}
                              field={`day:${d}`}
                              activeField={weekSortField}
                              direction={weekSortDirection}
                              onSort={handleWeekSort}
                              align="center"
                            />
                          ))}
                          <SortableHeaderCell label="Worked Hrs" field="workedHours" activeField={weekSortField} direction={weekSortDirection} onSort={handleWeekSort} align="right" />
                          <SortableHeaderCell label="Coverage Hrs" field="coverageHours" activeField={weekSortField} direction={weekSortDirection} onSort={handleWeekSort} align="right" />
                          <SortableHeaderCell label="OT Hrs" field="overtimeHours" activeField={weekSortField} direction={weekSortDirection} onSort={handleWeekSort} align="right" />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {sortedWeekRows.map((row: any) => (
                          <TableRow key={`${row.employeeId}_${row.campaignId}`} hover>
                            <TableCell
                              sx={{ position: 'sticky', left: 0, bgcolor: '#fff', zIndex: 1, cursor: 'pointer' }}
                              onClick={() => drillEmployee(row.employeeId, week.weekStart)}
                            >
                              <Tooltip title="Open Employee Timeline">
                                <Typography variant="body2" color="primary">{row.employeeName}</Typography>
                              </Tooltip>
                            </TableCell>
                            <TableCell
                              sx={{ position: 'sticky', left: 140, bgcolor: '#fff', zIndex: 1, cursor: 'pointer' }}
                              onClick={() => drillCampaign(row.campaignId, week.weekStart)}
                            >
                              {row.campaignName}
                            </TableCell>
                            <TableCell align="right" sx={{ position: 'sticky', left: 260, bgcolor: '#fff', zIndex: 1 }}>
                              {row.weeklyTotals.scheduledHours}
                            </TableCell>
                            <TableCell align="right" sx={{ position: 'sticky', left: 360, bgcolor: '#fff', zIndex: 1 }}>
                              {row.weeklyTotals.billableHours ?? 0}
                            </TableCell>
                            {weekDates.map((d) => renderDayCell(row.days[d], row, d))}
                            <TableCell align="right">{row.weeklyTotals.workedHours}</TableCell>
                            <TableCell align="right">{row.weeklyTotals.coverageHours}</TableCell>
                            <TableCell align="right">{row.weeklyTotals.overtimeHours}</TableCell>
                          </TableRow>
                        ))}
                        {sortedWeekRows.length > 0 && (
                          <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                            <TableCell sx={{ fontWeight: 700, position: 'sticky', left: 0, bgcolor: '#f5f5f5', zIndex: 1 }}>
                              Total
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700, position: 'sticky', left: 140, bgcolor: '#f5f5f5', zIndex: 1 }} />
                            <TableCell align="right" sx={{ fontWeight: 700, position: 'sticky', left: 260, bgcolor: '#f5f5f5', zIndex: 1 }}>
                              {weekTotals.scheduledHours}
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, position: 'sticky', left: 360, bgcolor: '#f5f5f5', zIndex: 1 }}>
                              {weekTotals.billableHours}
                            </TableCell>
                            {weekDates.map((d) => (
                              <TableCell key={d} align="center" sx={{ fontWeight: 700 }}>
                                <Typography variant="caption" display="block">
                                  {weekTotals.dayTotals[d].billingHours > 0
                                    ? `${weekTotals.dayTotals[d].billingHours}h bill`
                                    : weekTotals.dayTotals[d].hoursWorked > 0
                                      ? `${weekTotals.dayTotals[d].hoursWorked}h`
                                      : '—'}
                                </Typography>
                              </TableCell>
                            ))}
                            <TableCell align="right" sx={{ fontWeight: 700 }}>{weekTotals.workedHours}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>{weekTotals.coverageHours}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>{weekTotals.overtimeHours}</TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            );
          })}

          <TablePagination
            component="div"
            count={report.pagination.total}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[25, 50, 100, 200]}
          />
        </>
      )}

      <Dialog open={Boolean(expandedCell)} onClose={() => setExpandedCell(null)} maxWidth="md" fullWidth>
        {expandedCell && (
          <>
            <DialogTitle>
              {expandedCell.employeeName} — {moment(expandedCell.date).format('dddd, MMM D, YYYY')}
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={2}>
                {[
                  ['Scheduled Shift', expandedCell.scheduleStart && expandedCell.scheduleEnd ? `${expandedCell.scheduleStart} – ${expandedCell.scheduleEnd}` : '—'],
                  ['Clock In', expandedCell.clockIn || '—'],
                  ['Clock Out', expandedCell.clockOut || '—'],
                  ['Attendance', expandedCell.attendanceLabel],
                  ['Original Campaign', expandedCell.originalCampaignName || '—'],
                  ['Worked Campaign', expandedCell.workedCampaignName || '—'],
                  ['Coverage Mode', expandedCell.coverageModeLabel || '—'],
                  ['Coverage Start', expandedCell.coverageStart || '—'],
                  ['Coverage End', expandedCell.coverageEnd || '—'],
                  ['Coverage Duration', `${(expandedCell.coverageDurationMinutes / 60).toFixed(2)} hrs`],
                  ['Covered Employee', expandedCell.coveredEmployeeName || '—'],
                  ['Covering Employee', expandedCell.coveringEmployeeName || '—'],
                  ['Coverage Reason', expandedCell.coverageReason || '—'],
                  ['Absence Reason', expandedCell.absenceReason || '—'],
                  ['Supervisor Notes', expandedCell.supervisorNotes || '—'],
                  ['Customer Notified', expandedCell.customerNotified ? 'Yes' : 'No'],
                  ['Notification Date', expandedCell.customerNotificationDate || '—'],
                  ['Notification Method', expandedCell.customerNotificationMethod || '—'],
                  ['Notification Sent By', expandedCell.customerNotificationSentBy || '—'],
                  ['Billable', expandedCell.billable ? 'Yes' : 'No'],
                  ['Billing Hours', expandedCell.billingHours],
                  ['Invoice Status', expandedCell.invoiceStatus],
                  ['Row ID (Invoice Link)', expandedCell.rowId],
                ].map(([label, value]) => (
                  <Grid item xs={12} sm={6} key={String(label)}>
                    <Typography variant="caption" color="text.secondary">{label}</Typography>
                    <Typography variant="body2">{value}</Typography>
                  </Grid>
                ))}
              </Grid>
              {expandedCell.breaks?.length > 0 && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2">Breaks</Typography>
                  {expandedCell.breaks.map((b: string, i: number) => (
                    <Typography key={i} variant="body2">{b}</Typography>
                  ))}
                </>
              )}
              {expandedCell.auditTrail?.length > 0 && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2">Audit Trail</Typography>
                  {expandedCell.auditTrail.map((line: string, i: number) => (
                    <Typography key={i} variant="body2">• {line}</Typography>
                  ))}
                </>
              )}
              <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                <Button size="small" onClick={() => drillEmployee(expandedCell.employeeId, expandedCell.date)}>
                  Employee Timeline
                </Button>
                <Button size="small" onClick={() => drillCampaign(expandedCell.campaignId, expandedCell.date)}>
                  Campaign Detail
                </Button>
                {expandedCell.coverageAssignmentId && (
                  <Button size="small" onClick={() => navigate(`/operations/attendance-coverage?date=${expandedCell.date}&coverageId=${expandedCell.coverageAssignmentId}`)}>
                    Coverage Detail
                  </Button>
                )}
              </Stack>
            </DialogContent>
          </>
        )}
      </Dialog>
    </Container>
  );
};

export default WeeklyStaffingCoverageReportPage;
